import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Share2 } from 'lucide-react';
import Button from '../Button';
import { svg } from '../../config/images';

/**
 * Course card for user area (Browse & My Courses) and public All Courses page.
 * course: { id, title, description, lessons[] }
 * isEnrolled: second action shows "Enrolled" instead of Enroll
 * onEnrol: Enroll now — payment / free enroll (logged-in, not enrolled)
 * viewCoursePath: View course link (e.g. `/user/browse/course/:id`)
 * loginToEnrol: guest — single "Enroll now" → /login
 * enrollLoading: disable Enroll while checkout is in progress
 * showShare: show share control (default true)
 * sharePath: path appended to origin for shared link (default `/user/courses/:id`)
 */
const CourseCard = ({
  course,
  isEnrolled,
  onEnrol,
  viewCoursePath,
  loginToEnrol,
  enrollLoading = false,
  showShare = true,
  sharePath,
}) => {
  const [shareHint, setShareHint] = useState('');

  /** Logged-in catalogue cards should always show View + Enrol, even if a parent forgets `viewCoursePath`. */
  const effectiveViewPath =
    viewCoursePath ||
    (!loginToEnrol && course?.id != null && course.id !== ''
      ? `/user/browse/course/${String(course.id)}`
      : undefined);

  const lessonCount = (course.lessons || []).length;
  const lessonsLabel = lessonCount === 1 ? '1 Lesson' : `${lessonCount} Lessons`;
  const bannerText = course.bannerText || course.title || 'Course';

  const handleShare = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const id = course?.id;
      if (!id) return;
      const path = sharePath || `/user/courses/${id}`;
      const url = `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
      const title = course.title || 'Course';
      const text = `Check out: ${title}`;

      if (typeof navigator !== 'undefined' && navigator.share) {
        try {
          await navigator.share({ title, text, url });
          setShareHint('');
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return;
        }
      }

      try {
        await navigator.clipboard.writeText(url);
        setShareHint('Link copied');
        window.setTimeout(() => setShareHint(''), 2200);
      } catch {
        setShareHint('Copy blocked — open link manually');
        window.setTimeout(() => setShareHint(''), 3000);
      }
    },
    [course?.id, course?.title, sharePath]
  );

  return (
    <div className="group bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-gray-100 hover:border-primary-300 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 relative my-3">
      {/* Top Accent Line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-500" />

      {/* Header Section - Image or Green with Pattern */}
      <div className="relative bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 px-5 pt-5 pb-6 overflow-hidden h-[180px]">
        {course.imageUrl ? (
          <img
            src={course.imageUrl}
            alt={course.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 opacity-10">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '20px 20px',
              }}
            />
          </div>
        )}
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -mr-12 -mt-12" />
        <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/5 rounded-full -ml-10 -mb-10" />

        <div className="absolute top-4 left-5 z-10">
          <span className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-white text-xs font-bold border border-white/30">
            <img
              src={svg.star}
              alt="star"
              className="w-3 h-3"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
            Premium Course
          </span>
        </div>

        {showShare && (
          <div className="absolute top-3 right-3 z-20 flex flex-col items-end gap-1">
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm border border-white/40 text-white hover:bg-white/30 transition-colors shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-700"
              aria-label={`Share ${course.title || 'course'}`}
              title="Share course"
            >
              <Share2 className="h-4 w-4" strokeWidth={2.25} aria-hidden />
            </button>
            {shareHint ? (
              <span className="text-[10px] font-semibold text-white drop-shadow-md bg-black/25 px-2 py-0.5 rounded-md max-w-[140px] text-right">
                {shareHint}
              </span>
            ) : null}
          </div>
        )}

        <div className="mt-8 mb-5 relative z-10">
          <p className="text-white text-sm font-bold leading-tight line-clamp-2 drop-shadow-lg">
            {bannerText}
          </p>
        </div>

        <div className="flex justify-center relative z-10">
          <div className="relative">
            <div className="w-14 h-14 rounded-xl bg-white/25 backdrop-blur-md flex items-center justify-center border-2 border-white/40 shadow-2xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Body Section - White */}
      <div className="px-5 py-5 bg-gradient-to-b from-white to-gray-50">
        <div className="mb-3 h-[60px] flex flex-col justify-start">
          <h3 className="text-lg font-extrabold text-gray-900 mb-2 line-clamp-2 leading-tight group-hover:text-primary-600 transition-colors min-h-[48px]">
            {course.title || 'Untitled course'}
          </h3>
          <div className="w-14 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full" />
        </div>

        <div className="mb-4 h-[48px]">
          <p className="text-gray-600 text-sm line-clamp-2 leading-relaxed">
            {course.description || 'No description'}
          </p>
        </div>

        {/* Lessons count */}
        <div className="grid grid-cols-2 gap-2.5 mb-4">
          <div className="relative overflow-hidden bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl px-3 py-2.5 border-2 border-primary-200">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-md flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-gray-500 font-medium uppercase">Lessons</div>
                <div className="text-xs font-bold text-gray-900 truncate">{lessonsLabel}</div>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden bg-gradient-to-br from-secondary-50 to-secondary-100 rounded-xl px-3 py-2.5 border-2 border-secondary-200">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-secondary-500 to-secondary-600 flex items-center justify-center shadow-md flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-gray-500 font-medium uppercase">Content</div>
                <div className="text-xs font-bold text-gray-900 truncate">Video lessons</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer: View course + Enroll now side by side (or Enrolled / login guest) */}
      <div className="relative bg-gradient-to-br from-primary-100 via-primary-50 to-primary-100 px-5 pt-4 pb-4 border-t-2 border-primary-200">
        <div className="flex flex-row gap-2">
          {/* View Course Button (Link or Placeholder) */}
          {loginToEnrol ? (
            <Link
              to={viewCoursePath || "/login"}
              className="flex-1 min-w-0 text-center py-2.5 px-2 rounded-xl font-extrabold text-sm border-2 border-primary-600 text-primary-700 bg-white hover:bg-primary-50 transition-colors"
            >
              View course
            </Link>
          ) : effectiveViewPath ? (
            <Link
              to={effectiveViewPath}
              className="flex-1 min-w-0 text-center py-2.5 px-2 rounded-xl font-extrabold text-sm border-2 border-primary-600 text-primary-700 bg-white hover:bg-primary-50 transition-colors"
            >
              View course
            </Link>
          ) : (
            <span className="flex-1 min-w-0 text-center py-2.5 px-2 rounded-xl font-extrabold text-sm bg-gray-100 text-gray-500 cursor-default select-none">
              View course
            </span>
          )}

          {/* Enrollment Button (Link for Guests, Action Button for Users) */}
          {loginToEnrol ? (
            <Link
              to="/login"
              className="flex-1 min-w-0 text-center py-2.5 px-2 rounded-xl font-extrabold text-sm shadow-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors flex items-center justify-center"
            >
              Enroll now
            </Link>
          ) : isEnrolled ? (
            <span
              className="flex-1 min-w-0 text-center py-2.5 px-2 rounded-xl font-extrabold text-sm bg-gray-200 text-gray-600 cursor-default select-none flex items-center justify-center"
              aria-disabled="true"
            >
              Enrolled
            </span>
          ) : onEnrol ? (
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={enrollLoading || isEnrolled}
              className="flex-1 min-w-0 !py-2.5 !font-extrabold !text-sm !shadow-lg"
              onClick={onEnrol}
            >
              {enrollLoading ? "Please wait…" : "Enroll now"}
            </Button>
          ) : (
            <span className="flex-1 min-w-0 text-center py-2.5 px-2 rounded-xl font-extrabold text-sm bg-gray-100 text-gray-500 cursor-default flex items-center justify-center">
              —
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseCard;
